import { Phone } from 'lucide-react'
import { forwardRef, useCallback, type ComponentProps, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { formatTrPhone } from '@/lib/phone'
import { cn } from '@/lib/utils'

export type PhoneInputProps = Omit<ComponentProps<'input'>, 'type' | 'value' | 'onChange' | 'defaultValue'> & {
  value?: string
  onChange?: (value: string) => void
  /** Varsayılan: +90 … */
  defaultCountryHint?: boolean
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value = '', onChange, className, placeholder, onBlur, disabled, ...props },
  ref,
) {
  const { t } = useTranslation()
  const display = value ? formatTrPhone(value) : ''

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(formatTrPhone(e.target.value))
    },
    [onChange],
  )

  return (
    <div className={cn('relative', disabled && 'opacity-50')}>
      <Phone
        className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        disabled={disabled}
        value={display}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder ?? t('common.phonePlaceholder')}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-card py-1 pr-3 pl-10 text-sm tabular-nums shadow-xs transition-colors',
          'placeholder:text-muted-foreground',
          'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/30',
          className,
        )}
        {...props}
      />
    </div>
  )
})
